import { useUser } from '@clerk/clerk-react'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { extractCustomDomainFromEmail } from '@saaga/shared/utils/email-domain.util'
import { pushGtmTutorialComplete } from '@saaga/shared/utils/gtm.util'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useOrgSlug } from '@/hooks/use-org-slug.hook'
import { hasDeepLinkArrivedInSession, hasDeepLinkIntent } from '@/lib/deep-link-intent.util'

import {
  readOnboardingStatus,
  shouldShowWizard,
  updateOnboardingMetadata,
} from './onboarding-metadata.helper'
import { generateOnboardingPrompt } from './onboarding-prompt.util'
import type { OnboardingWizardFormData } from './onboarding-wizard.interface'

const SEO_AGENCY_SLUG = 'seo-pals'

interface UseOnboardingWizardResult {
  isOpen: boolean
  isLoading: boolean
  defaultWebsiteUrl: string
  handleClose: () => void
  handleSkip: () => Promise<void>
  handleSubmit: (data: OnboardingWizardFormData) => Promise<void>
}

export const useOnboardingWizard = (): UseOnboardingWizardResult => {
  const { user } = useUser()
  const navigate = useNavigate()
  const orgSlug = useOrgSlug()
  const [isLoading, setIsLoading] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)

  const currentStatus = user ? readOnboardingStatus(user.unsafeMetadata) : undefined
  const isOpen =
    !isDismissed &&
    !hasDeepLinkIntent() &&
    !hasDeepLinkArrivedInSession() &&
    shouldShowWizard(currentStatus)

  const defaultWebsiteUrl = useMemo(() => {
    const email = user?.primaryEmailAddress?.emailAddress
    if (!email) return ''
    return extractCustomDomainFromEmail(email) ?? ''
  }, [user?.primaryEmailAddress?.emailAddress])

  const handleClose = useCallback((): void => {
    trackEvent('onboarding_closed', {})
    setIsDismissed(true)
  }, [])

  const handleSkip = useCallback(async (): Promise<void> => {
    if (!user) return
    setIsLoading(true)
    trackEvent('onboarding_skipped', {})
    await updateOnboardingMetadata(user, 'skipped')
    setIsLoading(false)
  }, [user])

  const handleSubmit = useCallback(
    async (data: OnboardingWizardFormData): Promise<void> => {
      if (!user) return
      setIsLoading(true)
      trackEvent('onboarding_completed', {})
      pushGtmTutorialComplete()
      await updateOnboardingMetadata(user, 'completed')
      setIsLoading(false)
      if (!orgSlug) return
      const prompt = generateOnboardingPrompt(data)
      navigate(`/${orgSlug}/chat/${SEO_AGENCY_SLUG}?prompt=${encodeURIComponent(prompt)}`)
    },
    [user, orgSlug, navigate]
  )

  return { isOpen, isLoading, defaultWebsiteUrl, handleClose, handleSkip, handleSubmit }
}
